import React, { useEffect, useState } from "react";
import { Table, Space, Tabs, Row, Col } from "antd";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../redux/Agent/actions";
import { EyeFilled, EyeInvisibleFilled } from "@ant-design/icons";
import AgentTransactionModal from "./Model/AgentTransaction";
import DateFilter from "../../utils/DatePicker";
import moment from "moment";

const { getActiveAgent, getAgentTransactions, getAgentTransactionsDetails } =
  actions;

const Agents = () => {
  const [agentTransactionModel, setAgentTransactionModel] = useState(false);
  const [dateRange, setDateRange] = useState({ fromDate: "", toDate: "" });

  // Pagination states
  const [activeAgentPage, setActiveAgentPage] = useState(1);
  const [activeAgentPageSize, setActiveAgentPageSize] = useState(10);
  const [agentTransactionPage, setAgentTransactionPage] = useState(1);
  const [agentTransactionPageSize, setAgentTransactionPageSize] = useState(10);
  const [selectedAgentName, setSelectedAgentName] = useState("");

  // Column definitions that use the pagination states
  const columnsActiveAgent = [
    {
      title: "#",
      dataIndex: "key",
      key: "key",
      width: 50,
      render: (_, record, index) =>
        (activeAgentPage - 1) * activeAgentPageSize + index + 1,
    },
    { title: "Agent Id", dataIndex: "agentId", key: "agentId" },
    { title: "Agent Name", dataIndex: "agentName", key: "agentName" },
  ];

  const columnsAgentTransactions = [
    {
      title: "#",
      dataIndex: "key",
      key: "key",
      width: 50,
      render: (_, record, index) =>
        (agentTransactionPage - 1) * agentTransactionPageSize + index + 1,
    },
    { title: "Agent Id", dataIndex: "agentId", key: "agentId" },
    { title: "Agent Name", dataIndex: "agentName", key: "agentName" },
    {
      title: "Total Transaction",
      dataIndex: "totalTransactionCount",
      key: "totalTransactionCount",
      render: (value) => `${value ? value : "-"}`,
    },
    {
      title: "Total Amount",
      dataIndex: "totalTransactionAmount",
      key: "totalTransactionAmount",
      render: (value) => `${value ? "₹ " + value : "-"}`,
    },
    {
      title: "Total Bank Commission",
      dataIndex: "totalBankCommission",
      key: "totalBankCommission",
      render: (value) => `${value ? "₹ " + value : "-"}`,
    },
    {
      title: "Total AI Commission",
      dataIndex: "totalAiCommission",
      key: "totalAiCommission",
      render: (value) => `${value ? "₹ " + value : "-"}`,
    },
  ];

  const handleDateChange = (dates) => {
    if (dates) {
      setDateRange({ fromDate: dates[0], toDate: dates[1] });
    } else {
      setDateRange([]);
    }
  };
  const dispatch = useDispatch();

  // Redux selectors
  const {
    activeAgentResponseData,
    agentTransactionsResponseData,
    loading,
    totalData,
    totalDataTransactions,
  } = useSelector((state) => ({
    activeAgentResponseData: state.agentDetailsReducer?.activeAgentResponseData,
    agentTransactionsResponseData:
      state.agentDetailsReducer?.agentTransactionsResponseData,
    loading: state.agentDetailsReducer?.loading,
    totalData: state.agentDetailsReducer?.totalData,
    totalDataTransactions: state.agentDetailsReducer?.totalDataTransactions,
  }));

  // Fetch Active Agents
  useEffect(() => {
    dispatch(getActiveAgent(activeAgentPage, activeAgentPageSize));
  }, [dispatch, activeAgentPage, activeAgentPageSize]);

  // Fetch Agent Transactions
  useEffect(() => {
    if (dateRange.fromDate && dateRange.toDate) {
      const payload = {
        startDate: moment(
          dateRange?.fromDate["$d"] || dateRange?.fromDate["_d"]
        ).format("YYYY-MM-DDTHH:mm:ss%2B05:30"),
        endDate: moment(
          dateRange?.toDate["$d"] || dateRange?.toDate["_d"]
        ).format("YYYY-MM-DDTHH:mm:ss%2B05:30"),
      };

      dispatch(
        getAgentTransactions(
          agentTransactionPage,
          agentTransactionPageSize,
          payload.startDate,
          payload.endDate
        )
      );
    } else {
      dispatch(
        getAgentTransactions(agentTransactionPage, agentTransactionPageSize)
      );
    }
  }, [dispatch, agentTransactionPage, agentTransactionPageSize, dateRange]);

  // const handleViewAgentTransaction = (data) => {
  //     setAgentTransactionModel(true);
  //     dispatch(getAgentTransactionsDetails(data?.agentId));
  // };

  const handleViewAgentTransaction = (data) => {
    setSelectedAgentName(data.agentName);
    setAgentTransactionModel(true);

    const agentId = data?.agentId;
    let startDate, endDate;

    // If dateRange is available, include it in the payload
    if (dateRange?.fromDate && dateRange?.toDate) {
      startDate = moment(
        dateRange?.fromDate["$d"] || dateRange?.fromDate["_d"]
      ).format("YYYY-MM-DDTHH:mm:ss%2B05:30");
      endDate = moment(
        dateRange?.toDate["$d"] || dateRange?.toDate["_d"]
      ).format("YYYY-MM-DDTHH:mm:ss%2B05:30");
    }

    dispatch(getAgentTransactionsDetails(agentId, startDate, endDate));
  };

  const activeAgentTableData = activeAgentResponseData?.data?.map(
    (agent, index) => ({
      key: agent.agentId, // Use agentId as the key since it's unique
      ...agent,
    })
  );

  const agentTransactionsTableData = agentTransactionsResponseData?.data?.map(
    (agent, index) => ({
      key: agent.agentId, // Use agentId as the key since it's unique
      ...agent,
    })
  );

  const items = [
    {
      key: "1",
      label: "Active Agents",
      children: (
        <Table
          columns={columnsActiveAgent}
          dataSource={activeAgentTableData || []}
          rowKey="agentId"
          loading={loading}
          pagination={{
            total: totalData || 0,
            current: activeAgentPage,
            pageSize: activeAgentPageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ["5", "10", "20", "50", "100", "500"],
            onChange: (page, pageSize) => {
              setActiveAgentPage(page);
              setActiveAgentPageSize(pageSize);
            },
          }}
        />
      ),
    },
    {
      key: "2",
      label: "Agent Transactions",
      children: (
        <>
          <Row justify="end">
            <DateFilter
              onDateChange={handleDateChange}
              selectDateRange={setDateRange}
            />
          </Row>
          <Table
            columns={[
              ...columnsAgentTransactions,
              {
                title: "Transaction Category",
                render: (_, record) => (
                  <Space>
                    <EyeFilled
                      style={{
                        fontSize: "18px",
                        color: "#073763",
                        marginLeft: "10px",
                      }}
                      onClick={() => handleViewAgentTransaction(record)}
                    />
                  </Space>
                ),
              },
            ]}
            dataSource={agentTransactionsTableData}
            rowKey="agentId"
            loading={loading}
            pagination={{
              total: totalDataTransactions || 0,
              current: agentTransactionPage,
              pageSize: agentTransactionPageSize,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ["5", "10", "20", "50", "100", "500"],
              onChange: (page, pageSize) => {
                setAgentTransactionPage(page);
                setAgentTransactionPageSize(pageSize);
              },
            }}
          />
        </>
      ),
    },
  ];

  return (
    <>
      <div style={{ padding: "8px 20px 0px 40px" }}>
        <Tabs
          style={{
            "--ant-tabs-tab-hover-color": "#2a901b",
            "--ant-tabs-tab-active-color": "#2a901b",
            "--ant-tabs-ink-bar-color": "#2a901b",
          }}
          defaultActiveKey="1"
          items={items}
          type="line"
          animated
        />
      </div>
      <AgentTransactionModal
        {...{
          agentTransactionModel,
          setAgentTransactionModel,
          agentName: selectedAgentName,
        }}
      />
    </>
  );
};

export default Agents;
